import React from "react";
import { Link } from "react-router-dom";
import { styles } from "../../layout";
import Figure from "../components/figure";
import CustomLink from "../components/hoverlink";
import friend from "./components/flip/friend.png";
import Lift from "./components/flip/lift.png";
import nokia from "./components/flip/nokia.png";

const FlipPhoneBlog: React.FC = () => {
  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.header}>I had a flip phone for 4 months</h1>

      <p style={styles.paragraph}>
        From Jan 1 2023 to May 2023 I had a Nokia Flip Phone. My brother Joe and
        I decided we wanted to detach from our smartphones and take a break from
        smartphone life. I want to go over my takeaways from the experience as
        well as any recommendations for anyone thinking to do something similar.
      </p>

      <h2 style={styles.subHeader}>Philosophy of the Flip Phone</h2>
      <p style={styles.paragraph}>
        If you are not familiar with the "
        <CustomLink
          href="https://www.reddit.com/r/dumbphones/"
          target="_blank"
          rel="noreferrer"
        >
          dumb phone
        </CustomLink>
        " movement, allow me to explain. A smartphone is one of the most
        revolutionary inventions of the 21st century. It has endless tools,
        apps, messaging platforms, and much more. It essentially allows everyone
        to have a supercomputer in their pocket 24/7. With the advancements in
        technology there have been some harmful effects, namely social media
        addiction. The average screen times per day have continued to rise to
        now over{" "}
        <CustomLink
          href="https://www.harmonyhit.com/phone-screen-time-statistics/"
          target="_blank"
          rel="noreferrer"
        >
          5 hours per day for Americans
        </CustomLink>
        .
      </p>
      <p style={styles.paragraph}>
        Additionally there was a component of dopamine detox as well. Before
        adopting the flip phone I had set my smart phone to black and white and
        would go a few weeks at a time without color. This reduced my screen
        time as well, and I noticed when I would switch back color on I could
        literally feel a surge of dopamine.
      </p>

      <p style={styles.paragraph}>
        The goal of adopting a dumb phone or flip phone was to completely remove
        the ability to waste time on your phone and spend time on other more
        enriching things. (We did this before the concept of "
        <CustomLink
          href="https://en.wikipedia.org/wiki/Doomscrolling"
          target="_blank"
          rel="noreferrer"
        >
          doomscrolling
        </CustomLink>
        " was popularized.) People now understand that phones are wasting their
        time.
      </p>

      <h2 style={styles.subHeader}>Choosing the Right Phone</h2>
      <p style={styles.paragraph}>
        Joe and I ended up landing on the Nokia 2780 after a bit of research.
        First it had bluetooth, maps, a camera, an internet browser, and was
        within our budget. We ended up purchasing them at Best Buy and put our
        sim cards on Jan 1.
      </p>
      <p style={styles.paragraph}>
        Depending on how long or how committed you are to the flip phone you
        are, there might be some other features that may be non-negotiable. For
        example we did not have Spotify or any music service. We had to download
        music and upload on a micro-sd card.{" "}
      </p>
      <Figure
        imageSrc={nokia}
        altText="Nokia 2780"
        caption="The Nokia 2780 was my flip phone of choice."
        size="small"
      />
      <h2 style={styles.subHeader}>Key Takeaways</h2>
      <p style={styles.paragraph}>
        I ended up keeping the flip phone for a full semester which was just
        about 5 months. I was going to travel abroad in Spain and figured I
        needed some more tech to navigate the city and take high quality
        pictures.{" "}
      </p>
      <h3 style={styles.subSubHeader}>Productivity Increase</h3>
      <p style={styles.paragraph}>
        Although counterintuitive the flip phone actually increased
        productivity. You would think, the smartphone such a powerful tool would
        allow you to get more done, but that is not the case as there is too
        much non-productive applications that can eat away at your time.{" "}
      </p>
      <p style={styles.paragraph}>
        I believe my productivity increased for the following reasons.
      </p>
      <ul style={styles.list}>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>I had less notifications</strong>
            <p>
              Notifications are designed to get your attention and make you
              switch tasks. My flip phone only allowed texts and calls. No
              email, no social media, no apps. Additionally since I was unable
              to keep up with group texts (and I made them green) so I was not
              up to date with what my friends were doing as much.
            </p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>I had more time to focus</strong>
            <p>
              Since I didn’t have the phone to check every few minutes I was
              forced to stay on task and focus. This trained my brain to just
              stay on task and keep pushing through. Additionally with the
              reduced screen time use I just in general had larger time blocks.
            </p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>More relaxing breaks</strong>
            <p>
              Part of productivity is taking breaks to cool off. With a smart
              phone you don’t really get as much rest as you pump your brain
              with more dopamine which actually activates and excites your brain
              when it should be resting. I used the time/breaks to read, play
              guitar, go outside, and other truly relaxing tasks that actually
              allowed me to rest.
            </p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Attention span</strong>
            <p>
              My attention span was able to increase. I spent more time
              listening to podcasts, reading, and in general just not consuming
              short form content. This allowed me to build back up the attention
              span muscle and spend more time doing one task at a time, which
              over times allows you to get more done.
            </p>
          </div>
        </li>
      </ul>

      <h3 style={styles.subSubHeader}>Detached</h3>
      <p style={styles.paragraph}>
        The flip phone definitely makes you more detached from others. We are
        more connected than ever with instant messaging platforms and Facetime;
        you can talk to other across the world as if they were right next to
        you. I see the detachment that the flip phone provides as a double edged
        sword. First, you are more detached from the stuff you need a break
        from, social media drama, people you don't want to talk to, etc. On the
        other hand, you aren’t in communication as much with people you are
        close with and can feel left out.
      </p>
      <p style={styles.paragraph}>
        Although the quantity of your communications drops significantly (mostly
        because it just takes too long to type) the quality of your
        communication goes way up. Since you don’t get to talk to people as
        much, a phone call goes a long way. I started to leave people messages
        if they couldn’t pick up or when they did have a longer conversation
        that allowed deeper connection.
      </p>
      <p style={styles.paragraph}>
        I did struggle with feeling isolated at times as I was forced by my own
        doing and external factors to not be as social during this time, but the
        interactions I did have were much more meaningful and I was grateful for
        the time I had.
      </p>

      <Figure
        imageSrc={friend}
        altText="Flip phone friendly text"
        caption="A friendly text after leaving a message."
        size="small"
      />

      <h3 style={styles.subSubHeader}>Other Hobbies or Activities</h3>
      <p style={styles.paragraph}>
        Having a flip phone means you have a little more time each day to spend
        time on hobbies or other things to focus on. I was able to spend a lot
        more time playing the guitar, reading, and watching longer youtube
        videos or documentaries.
      </p>
      <p style={styles.paragraph}>
        I also had more time to focus on extracurricular activities, for example
        landing an internship. I have a blog about the full process linked{" "}
        <CustomLink
          href="https://jack-pearson.com/blog/how-to-get-a-job"
          target="_blank"
          rel="noreferrer"
        >
          here
        </CustomLink>
        , but this semester was when I really spent time networking and was able
        to land a great internship for myself over a year in advance. Almost all
        my networking calls were taken on the flip phone (over 50+ calls).
      </p>

      <h3 style={styles.subSubHeader}>Things I Noticed About Society</h3>
      <p style={styles.paragraph}>
        When everyone in society has a smart phone,{" "}
        <CustomLink
          href="https://www.pewresearch.org/internet/fact-sheet/mobile/"
          target="_blank"
          rel="noreferrer"
        >
          around 91% own a smartphone
        </CustomLink>
        . When you don’t have a phone you can notice things around you that
        otherwise you wouldn’t have because you were texting or just scrolling.
      </p>
      <p style={styles.paragraph}>
        The first thing I noticed is how whenever we are waiting for something
        we are on our phones. For example I recall a few times being on a bus
        and being the only person looking around, everyone else was glued to
        their phones. This also applied to like waiting in line, commercial
        breaks, or for class to start.
      </p>
      <p style={styles.paragraph}>
        The next thing I noticed is that sometimes in group setting, like at
        lunch or other times, people take a break to sometimes check their
        phone. Once the first person does, the rest followed and it was like a
        group phone break, where I would be left to watch. It was quite
        interesting to say the least.{" "}
      </p>

      <h3 style={styles.subSubHeader}>Downsides of a Flip Phone</h3>

      <p style={styles.paragraph}>
        As I mentioned the smart phone is a powerful tool. To give it up
        certainly had some downsides.
      </p>
      <ul style={styles.list}>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Camera</strong>
            <p>
              The camera was much worse and is increasingly necessary for some
              tasks. For example, scanning QR codes was something I struggled
              with. Taking pictures also decreased.
            </p>
            <p>
              Check out my instagram recap{" "}
              <CustomLink
                href="https://www.instagram.com/p/Crtrr9apkad/?img_index=1"
                target="_blank"
                rel="noreferrer"
              >
                here
              </CustomLink>
            </p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Music/Podcasts</strong>
            <p>
              As I mentioned downloading music was much worse compared to
              Spotify or audible.
            </p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Apps</strong>
            <p>
              No rideshare or city transit apps could be downloaded. Traveling
              would have been almost impossible unless with company.
            </p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Maps</strong>
            <p>
              The map app existed but was quite limited. I drove 3 hours to
              visit a friend at school and missed an exit for over 30 mins since
              I read a number wrong.
            </p>
          </div>
        </li>
      </ul>

      <p style={styles.paragraph}>
        Those are the main tactical downsides of the flip phone. But in general
        the feeling of isolation and constant explanation of why you had a flip
        phone was sometimes annoying. People also liked to make fun of you which
        was mostly just funny. Many people thought it was super cool and wanted
        to learn more and said they had always wanted to get one as well.
      </p>

      <p style={styles.paragraph}>
        I hope you found this interesting and consider a challenge similar to
        this. But from my experience it might be better to turn your phone black
        and white or just delete the apps you don’t wanna waste time on.
      </p>
      <p>- Jack</p>
      <p style={styles.date}>Published on July 5, 2025</p>

      <h3 style={styles.subHeader}>
        P.S I also lifted weights during this time!
      </h3>
      <div style={styles.note}>
        If you want to use my free workout tracker to track your lifts click{" "}
        <CustomLink href="/landing" target="_blank" rel="noreferrer">
          here
        </CustomLink>
      </div>
      <p style={styles.paragraph}>
        Another benefits of the flip phone is that I would focus a lot more on
        my lifts and workouts!
      </p>
      <Figure
        imageSrc={Lift}
        altText="Flip phone workout"
        caption="Pump captured on flip phone."
        size="small"
      />

      <Link to="/blogs" style={styles.backLink}>
        Back to Blogs
      </Link>
    </div>
  );
};

export default FlipPhoneBlog;
